import { useCallback, useEffect, useState } from "react";
import { axiosInstance } from "../../utils/axiosInstance";
import {
  EngineerVisit,
  EngineerVisitInput,
  EngineerVisitsPayload,
} from "./engineerVisitTypes";

const BASE = "/ops/engineer-visits";

export function engineerVisitsErrorMessage(err: any, fallback: string): string {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
}

export async function createVisit(input: EngineerVisitInput): Promise<EngineerVisit> {
  const res = await axiosInstance.post<EngineerVisit>(BASE, input);
  return res.data;
}

export async function updateVisit(
  id: string,
  input: EngineerVisitInput
): Promise<EngineerVisit> {
  const res = await axiosInstance.patch<EngineerVisit>(`${BASE}/${id}`, input);
  return res.data;
}

export async function deleteVisit(id: string): Promise<void> {
  await axiosInstance.delete(`${BASE}/${id}`);
}

export function useEngineerVisits(opts: { locationId?: number | null; limit?: number } = {}) {
  const { locationId = null, limit } = opts;
  const [data, setData] = useState<EngineerVisitsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const reload = useCallback(
    async (reloadOpts?: { soft?: boolean }) => {
      if (reloadOpts?.soft) setRefreshing(true);
      else setLoading(true);
      setError(null);
      try {
        const res = await axiosInstance.get<EngineerVisitsPayload>(BASE, {
          params: {
            ...(locationId != null ? { locationId } : {}),
            ...(limit != null ? { limit } : {}),
          },
        });
        setData(res.data);
      } catch (err: any) {
        setError(engineerVisitsErrorMessage(err, "Failed to load engineer visits."));
        if (!reloadOpts?.soft) setData(null);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [locationId, limit]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, loading, error, refreshing, reload };
}
